<script lang="ts" setup>
import { ElMessage, ElNotification } from 'element-plus'
import type { AxiosError } from 'axios'
import CapacityAlert from './CapacityAlert.vue'
import BaseDialog from './BaseDialog.vue'
import type { AppointmentSlot } from '~/models/appointmentSlot'
import { AppointmentBookingApi, TerminalName } from '~/services/apiClient/api'
import type { SlotNotFoundResponse } from '~/services/apiClient/api'
import BookingForm from '~/components/inputs/BookingForm.vue'
import AppointmentSlotWarningIcon from '~/components/appointmentSlots/AppointmentSlotWarningIcon.vue'
import type { Appointment } from '~/models/appointments'
import type { Container } from '~/models/containers'
import type { RemainingCapacityPeriodInfo } from '~/stores/remainingCapacities'
import { useRemainingCapacitiesStore } from '~/stores/remainingCapacities'
import { useApptAvailabilityStore } from '~/stores/appointmentSlots'

const props = defineProps<{
  appointmentSlot: AppointmentSlot
  existingAppointment: Appointment | null
  container: Container
}>()

const emit = defineEmits<{ (e: 'close'): void }>()
const slot = toRef(props, 'appointmentSlot')
const remainingCapacitiesStore = useRemainingCapacitiesStore()
const apptAvailabilityStore = useApptAvailabilityStore()

const bucketsWithRemainingCapacity = computed(
  (): RemainingCapacityPeriodInfo[] => {
    if (!remainingCapacitiesStore.loaded) return []
    return remainingCapacitiesStore.getRemainingCapacities(
      slot.value.window_start
    )
  }
)
const remainingCapacity = computed((): number | null => {
  if (!remainingCapacitiesStore.loaded) return null
  return remainingCapacitiesStore.getRemainingCapacityForTime(
    slot.value.window_start
  )
})
const dialogOpen = ref(true)
type BookingFormType = InstanceType<typeof BookingForm>
const bookingFormRef = ref<BookingFormType>()

const submitForm = async (formEl: BookingFormType | undefined) => {
  if (!formEl) return
  formEl.validate((valid: boolean) => {
    if (valid) {
      const form = formEl.getValues()
      if (props.existingAppointment) {
        let errorMsg = null as null | string
        if (
          props.existingAppointment.terminal !== props.appointmentSlot.terminal
        ) {
          errorMsg =
            'Existing appointment terminal does not match appointment slot terminal'
        } else if (
          props.existingAppointment.container_number !==
          props.appointmentSlot.container_number
        ) {
          errorMsg = `Appointment slot container number does not match existing appointment`
        }
        if (errorMsg) {
          ElMessage.warning(errorMsg)
          throw new Error(errorMsg)
        }
      }
      const bookingAPI = new AppointmentBookingApi()
      bookingAPI
        .bookAppointmentAppointmentsBookingBookPost(
          {
            slot: {
              window_start: slot.value.window_start.toUTC().toISO(),
              terminal: slot.value.terminal,
              container_number: props.container.number,
              from_terminal_block_availability_key:
                slot.value.from_terminal_block_availability_key,
              observed: slot.value.observed.toUTC().toISO(),
            },
            master_bill_of_lading: form.masterBL,
            truck_license_plate: form.licensePlate,
            existing_appointment_id_to_cancel: props.existingAppointment?.id,
            own_chassis: form.ownChassis,
          },
          {
            skipErrorHandling: true,
          }
        )
        .then((resp) => {
          // Is there a better way to check for error?
          if (resp) {
            let title
            if (props.existingAppointment) {
              title = 'Appointment Reschedule begun'
            } else {
              title = 'Appointment Booking begun'
            }
            ElNotification({
              title,
              message: 'You will get a notification on success or failure',
              type: 'success',
            })
          }
          closeDialog()
        })
        .catch((err: AxiosError) => {
          if (err.response?.status === 404) {
            // Handle slot not found with new availability
            const errorData = err.response.data as SlotNotFoundResponse
            apptAvailabilityStore.updateSlotsFromAvailability(
              errorData.new_availability
            )
            ElNotification.error({
              title: 'Time no longer available',
              message: 'Please select a different time',
            })
            closeDialog()
            return true
          }
          // For other errors, use default error handling
          throw err
        })
    }
  })
}
function closeDialog() {
  dialogOpen.value = false
  emit('close')
}
const title = computed(() => {
  if (props.existingAppointment) {
    return 'Reschedule Appointment'
  } else {
    return 'Book Appointment'
  }
})
const bookButtonType = computed((): 'success' | 'warning' | 'danger' => {
  if (props.existingAppointment) {
    return 'warning'
  } else if (remainingCapacity.value !== null && remainingCapacity.value <= 0) {
    return 'danger'
  } else {
    return 'success'
  }
})
// We are using "magic" text to detect the ghost appointment case. While this is a bit
// brittle, the dependency is noted in the backend code and if it breaks we will just
// use the default warning text, which is fine. I did this because I didn't want to
// bloag the slots API with a field like "is_ghost" and as this is already a very
// special case I'm down to also special case it here.
const slotWarningTitle = computed((): string | null => {
  if (props.appointmentSlot.warning) {
    if (props.appointmentSlot.warning.includes('not bookable')) {
      return 'Potential "ghost" appointment time (not bookable)'
    }
    return props.appointmentSlot.warning
  } else {
    return null
  }
})
const slotWarningBody = computed((): string | null => {
  if (
    props.appointmentSlot.warning &&
    props.appointmentSlot.warning.includes('not bookable') &&
    props.appointmentSlot.terminal === TerminalName.Apm
  ) {
    return `You can try booking but it will likely fail.
    <br><br>
    There is an issue with APM TERMPoint where certain appointment times get
    'stuck' and it looks like you can book an appointment but you will always get an
    error when booking. We've detected that this is one of those times.`
  } else {
    return null
  }
})
</script>

<template>
  <BaseDialog
    :model-value="dialogOpen"
    :title="title"
    :min-width="500"
    :proportional-width="0.3"
    :max-width="600"
    center
    :append-to-body="true"
    @close="emit('close')"
  >
    <div>
      <BookingForm
        ref="bookingFormRef"
        :container="props.container"
        :appointment-slot="slot"
        :inline="false"
        size="default"
      />
      <el-alert
        v-if="props.container.import_status?.available_for_pickup === false"
        :closable="false"
        type="warning"
        show-icon
        class="shadow mt-4"
        style="margin-bottom: 8px"
        title="Container not ready for pickup"
      >
        You can book an still book an appointment, but something else must
        happen prior to pickup (e.g. clear hold)
      </el-alert>
      <CapacityAlert
        v-for="bucketWithCapacity in bucketsWithRemainingCapacity"
        :key="bucketWithCapacity.set.key"
        :remaining-capacity="bucketWithCapacity"
      />
      <el-alert
        v-if="props.appointmentSlot.warning"
        :closable="false"
        type="error"
        class="shadow mt-4"
        style="margin-bottom: 8px"
      >
        <template #title>
          <AppointmentSlotWarningIcon
            :warning="props.appointmentSlot.warning"
            class="mr-1 ml-0 mb-1px text-base align-text-bottom"
          />
          {{ slotWarningTitle }}
        </template>
        <template v-if="slotWarningBody" #default>
          <div v-html="slotWarningBody" />
        </template>
      </el-alert>
    </div>
    <template #footer>
      <div class="flex">
        <el-button
          size="large"
          class="flex-1 fs-close-booking-dialog"
          @click="closeDialog"
        >
          Cancel
        </el-button>
        <el-button
          :type="bookButtonType"
          size="large"
          class="flex-1"
          :class="{
            'fs-book-appointment': !props.existingAppointment,
            'fs-reschedule-appointment': props.existingAppointment,
          }"
          @click="submitForm(bookingFormRef)"
        >
          <template v-if="props.existingAppointment">
            Reschedule Appointment
          </template>
          <template
            v-else-if="remainingCapacity !== null && remainingCapacity <= 0"
          >
            Book Anyway
          </template>
          <template v-else> Book </template>
        </el-button>
      </div>
    </template>
  </BaseDialog>
</template>
